import { userDisplayLabel } from "@lumika/shared";
import type { User } from "@lumika/shared";

/**
 * Best human label for a user, in priority order: first + last name →
 * display name → email → phone → "Unknown user".
 *
 * Shared so every activity feed, assignment chip, and the People table
 * names an actor by their actual first/last name (or email/phone) — never
 * a bare set of initials or a raw UUID. Accepts any object carrying the
 * name fields so callers can pass a full `User` or a lighter shape.
 * Delegates to the cross-app canonical chain in @lumika/shared.
 */
export function userDisplayName(
  user: Pick<User, "firstName" | "lastName" | "displayName" | "email"> & {
    phone?: string | null;
  },
): string {
  return userDisplayLabel(user);
}

/**
 * The users a picker may offer as an assignee.
 *
 * Every assign guard on the server — `assertAssignableUser` (leads,
 * contacts, follow-ups), `TasksService.assertAssignee`,
 * `AppointmentAccessService.assertAssignee`, `AutomationsService.assertUser`
 * — rejects the same two: a DEACTIVATED user, because work assigned to
 * someone who can no longer log in silently rots, and a SYSTEM user (Lumika
 * staff), whose flag is a visibility bypass rather than a seat on the team.
 * `pending` is deliberately assignable: a newly invited teammate can receive
 * work right away, and the invite email covers awareness.
 *
 * This existed as a hand-rolled filter at a dozen call sites and eight had
 * drifted — offering people the server then refused, so the operator picked a
 * name, saved, and got "Assignee must be a non-deactivated user in this
 * dealership". Keep it here so a picker cannot silently disagree with the
 * guard behind it.
 *
 * `allowAi` opts a surface into offering the dealership AI. Assigning a LEAD
 * to the AI is a product feature, so lead pickers pass it; task, appointment
 * and automation guards all require `kind='human'` and must not.
 */
export function assignableUsers(
  users: readonly User[],
  opts: { allowAi?: boolean } = {},
): User[] {
  return users.filter(
    (u) =>
      u.status !== "inactive" &&
      !u.isSystemUser &&
      (u.kind === "human" || (opts.allowAi === true && u.kind === "ai")),
  );
}

/**
 * Membership roles are stored as lowercase snake_case enums
 * (MembershipRoleSchema). This maps each to the label a human should
 * actually read — "Owner", "Sales manager", "Read-only" — so the user
 * menu and any role chip never surface a raw `bdc_agent` token the way
 * the old sidebar footer did.
 *
 * Falls back to a de-snaked, sentence-cased form for any role the map
 * doesn't yet know, so a newly added backend role degrades to readable
 * text instead of a blank line or a raw enum.
 */
const ROLE_LABELS: Record<string, string> = {
  dealership_owner: "Owner",
  dealership_admin: "Admin",
  sales_manager: "Sales manager",
  service_manager: "Service manager",
  bdc_agent: "BDC agent",
  sales_agent: "Sales agent",
  service_agent: "Service agent",
  readonly: "Read-only",
};

export function humanRoleLabel(role: string): string {
  const known = ROLE_LABELS[role];
  if (known) return known;
  const spaced = role.replace(/_/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : "Member";
}
